import React from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  DialogTitleProps,
  styled,
} from '@mui/material';
import { Button, CustomButtonProps } from 'components/Button/Button';
import { useDeviceType } from 'contexts/ThemeContext/hooks/useDeviceType';
import { useLayoutElements } from 'contexts/LayoutElementsContext/useLayoutElements';

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'uppercase',
}));

export interface ConfirmationDialogProps extends DialogProps {
  open: boolean;
  title: string;
  description?: string;
  handleYes: () => void;
  handleNo: () => void;
  onClose?: () => void;
  yesButtonLabel?: string;
  noButtonLabel?: string;
  noButtonProps?: CustomButtonProps;
  yesButtonProps?: CustomButtonProps;
  children?: React.ReactNode;
  dialogTitleProps?: DialogTitleProps;
  hideBackdrop?: boolean;
}

export const ConfirmationDialog = ({
  open,
  title,
  description,
  handleYes,
  handleNo,
  onClose,
  yesButtonLabel,
  noButtonLabel,
  children,
  container,
  noButtonProps,
  yesButtonProps,
  dialogTitleProps,
  hideBackdrop,
  ...restProps
}: ConfirmationDialogProps) => {
  const { isDesktop } = useDeviceType();
  const { isAlertOpen } = useLayoutElements();

  const paperProps = {
    ...restProps?.PaperProps,
    sx: {
      // Match the default dialog width on Android
      // https://m3.material.io/components/dialogs/specs
      minWidth: '280px',
      ...restProps?.PaperProps?.sx,
    },
  };

  return (
    <Dialog
      open={open}
      onClose={onClose ?? handleNo}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      className="confirmation-dialog"
      container={isDesktop ? container : undefined}
      disableEscapeKeyDown={isAlertOpen} // Prevent closing if an alert is open
      disableEnforceFocus // Prevent focus trap issues on mobile devices
      hideBackdrop={hideBackdrop}
      {...restProps}
      PaperProps={paperProps}
    >
      {title && (
        <DialogTitle {...dialogTitleProps} id="confirmation-dialog-title">
          {title}
        </DialogTitle>
      )}
      <DialogContent>
        {description && (
          <DialogContentText id="confirmation-dialog-description">{description}</DialogContentText>
        )}
        {children}
      </DialogContent>
      <DialogActions>
        {noButtonLabel && (
          <StyledButton {...noButtonProps} onClick={handleNo}>
            {noButtonLabel}
          </StyledButton>
        )}
        {yesButtonLabel && (
          <StyledButton {...yesButtonProps} onClick={handleYes}>
            {yesButtonLabel}
          </StyledButton>
        )}
      </DialogActions>
    </Dialog>
  );
};
